/**
 * IntroScreen — Story tutorial 15 giây (3 scenes × 5s).
 *
 * 3 ảnh nền tận dụng từ design/ (đã resize):
 *   /assets/scenes/scene_1.jpg — concept đồ họa tổng thể (quán cà phê Việt).
 *   /assets/scenes/scene_2.jpg — background quán (xe đẩy cà phê).
 *   /assets/scenes/scene_3.jpg — gameplay ShopScreen ref.
 *
 * Nút "Bỏ qua" hiện từ giây thứ 3. Hết 15s tự chuyển /onboarding/name.
 *
 * Tham chiếu:
 *   - `docs/analysis/02_use_cases.md` UC-03.
 *   - design doc v6.1 §22 + technical spec §17.0.
 */
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { unlock } from "../lib/sound";

const SCENE_KEYS = [
  { key: "intro.scene_1", bg: "/assets/scenes/scene_2.jpg" },
  { key: "intro.scene_2", bg: "/assets/scenes/scene_1.jpg" },
  { key: "intro.scene_3", bg: "/assets/scenes/scene_3.jpg" },
];

const SCENE_DURATION_MS = 5_000;
const SKIP_VISIBLE_AFTER_MS = 3_000;
const TOTAL_MS = SCENE_DURATION_MS * SCENE_KEYS.length;

export function IntroScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sceneIdx, setSceneIdx] = useState(0);
  const [showSkip, setShowSkip] = useState(false);

  useEffect(() => {
    const skipTimer = setTimeout(() => setShowSkip(true), SKIP_VISIBLE_AFTER_MS);
    const sceneTimer = setInterval(() => {
      setSceneIdx((i) => Math.min(i + 1, SCENE_KEYS.length - 1));
    }, SCENE_DURATION_MS);
    const endTimer = setTimeout(() => {
      navigate("/onboarding/name");
    }, TOTAL_MS);

    return () => {
      clearTimeout(skipTimer);
      clearInterval(sceneTimer);
      clearTimeout(endTimer);
    };
  }, [navigate]);

  function handleSkip() {
    unlock();
    navigate("/onboarding/name");
  }

  const scene = SCENE_KEYS[sceneIdx];

  return (
    <main className="min-h-full relative overflow-hidden bg-coffee">
      <AnimatePresence mode="wait">
        <motion.div
          key={sceneIdx}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${scene.bg})` }}
        />
      </AnimatePresence>

      {/* Dim overlay để text dễ đọc */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/20 pointer-events-none" />

      {showSkip && (
        <button
          type="button"
          onClick={handleSkip}
          className="absolute top-4 right-4 text-sm text-white/90 underline bg-black/40 px-3 py-1 rounded-full backdrop-blur-sm z-10"
          data-testid="intro-skip"
        >
          {t("intro.skip")}
        </button>
      )}

      <div className="relative min-h-full flex flex-col items-center justify-end px-6 pb-24 pt-8 z-10">
        <AnimatePresence mode="wait">
          <motion.p
            key={sceneIdx}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.6 }}
            className="text-center text-xl md:text-2xl text-white font-medium leading-relaxed max-w-xl drop-shadow-lg"
          >
            {t(scene.key)}
          </motion.p>
        </AnimatePresence>

        <div className="absolute bottom-8 flex gap-2">
          {SCENE_KEYS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === sceneIdx ? "w-8 bg-white" : "w-3 bg-white/40"
              }`}
            />
          ))}
        </div>
      </div>
    </main>
  );
}
